/**
 * Exporta comisiones (earnings, summaries, settlements) a CSV.
 */
import { buildCsvFromRows } from '../../shared/csv-core/infra/buildCsv'
import { shareOrDownloadCsv } from '../../shared/csv-core/infra/shareCsv';
import type {
  CommissionSummary,
  CommissionEarning,
  CommissionSettlement,
} from '../domain/commissionTypes';

function formatMoney(n: number): string {
  return n.toFixed(2);
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-AR');
  } catch {
    return iso;
  }
}

export async function exportCommissionsSummaryToCsv(
  summaries: CommissionSummary[],
  filename = 'comisiones-resumen.csv',
): Promise<void> {
  const headers = ['Empleado', 'Comisiones', 'Bruto', 'Pendiente', 'Ajustes', 'A liquidar'];
  const rows = summaries.map((s) => [
    s.employeeName,
    String(s.earningsCount),
    formatMoney(s.grossEarnings),
    formatMoney(s.pendingEarnings),
    formatMoney(s.adjustmentsTotal),
    formatMoney(s.netPayable),
  ]);
  const csv = buildCsvFromRows(headers, rows);
  await shareOrDownloadCsv(csv, filename);
}

export async function exportCommissionsEarningsToCsv(
  earnings: CommissionEarning[],
  filename = 'comisiones-detalle.csv',
): Promise<void> {
  const headers = [
    'Empleado',
    'Descripción',
    'Base',
    'Porcentaje',
    'Monto',
    'Estado',
    'Fecha',
  ];
  const rows = earnings.map((e) => [
    e.employeeName,
    e.description ?? '',
    formatMoney(e.commissionableAmount),
    e.ratePercent != null ? String(e.ratePercent) : '',
    formatMoney(e.commissionAmount),
    e.status,
    formatDate(e.earnedAt),
  ]);
  const csv = buildCsvFromRows(headers, rows);
  await shareOrDownloadCsv(csv, filename);
}

export async function exportSettlementsToCsv(
  settlements: CommissionSettlement[],
  filename = 'liquidaciones.csv',
): Promise<void> {
  const headers = [
    'Empleado',
    'Desde',
    'Hasta',
    'Bruto',
    'Ajustes',
    'Neto',
    'Estado',
    'Método de pago',
    'Aprobada',
    'Pagada',
  ];
  const rows = settlements.map((s) => [
    s.employeeName,
    formatDate(s.periodFrom),
    formatDate(s.periodTo),
    formatMoney(s.grossEarnings),
    formatMoney(s.adjustmentsTotal),
    formatMoney(s.netPayable),
    s.status,
    s.paymentMethod ?? '',
    s.approvedAt ? formatDate(s.approvedAt) : '',
    s.paidAt ? formatDate(s.paidAt) : '',
  ]);
  const csv = buildCsvFromRows(headers, rows);
  await shareOrDownloadCsv(csv, filename);
}
